import { SystemStyleObject } from "@chakra-ui/react";
import { typography } from "./tokens";

/**
 * Spread onto <Text>/<Td> elements displaying numeric data (prices, counts, dates, IDs).
 * Uses JetBrains Mono in Clubhouse aesthetic via CSS custom property.
 */
export const monoDataProps = {
    fontFamily: 'var(--mono-font, inherit)',
    sx: { fontFeatureSettings: '"tnum" 1' },
} as const;

export const tableHoverStyle: SystemStyleObject = {
    bg: 'brand.50',
    transition: 'background 0.15s ease',
};

// ---------------------------------------------------------------------------
// Reusable table styling props — spread onto Chakra table sub-components
// to avoid copy-pasting the same values across every table.
// ---------------------------------------------------------------------------

/** Props for <Text> inside column header definitions — scoreboard style */
export const tableHeaderTextProps = {
    fontFamily: '"Urbanist", "Figtree", sans-serif',
    fontWeight: '700' as const,
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: 'secondaryGray.600',
} as const;

/** Props for <Th> elements — scoreboard underline instead of gray background */
export const tableThProps = {
    pe: '10px',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
    bg: 'transparent',
    borderBottom: '2px solid',
    borderColor: 'brand.400',
    opacity: 0.85,
} as const;

/** Props for <Flex> wrapping the header content inside <Th> */
export const tableThFlexProps = {
    justifyContent: 'space-between' as const,
    align: 'center' as const,
    fontFamily: '"Urbanist", "Figtree", sans-serif',
    fontWeight: '700' as const,
    fontSize: '11px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: 'secondaryGray.600',
};

/** Props for clickable <Tr> body rows */
export const tableRowProps = {
    _hover: {
        bg: 'brand.50',
        cursor: 'pointer',
    },
    transition: 'background 0.15s ease',
} as const;

/** Props for <Td> body cells */
export const tableCellProps = {
    fontSize: 'sm' as const,
    minW: { sm: '150px', md: '200px', lg: 'auto' },
    borderColor: 'transparent',
} as const;

/**
 * Props for <Td> cells displaying numeric data (prices, counts, percentages, IDs).
 * Uses JetBrains Mono with tabular numerals in Clubhouse aesthetic.
 * Spread onto Td: <Td {...tableCellNumericProps}>€12,450</Td>
 */
export const tableCellNumericProps = {
    ...tableCellProps,
    ...monoDataProps,
    textAlign: 'right' as const,
} as const;

/** Props for the outer <Table> element */
export const tableProps = {
    variant: 'simple' as const,
    color: 'gray.500',
    mb: 6,
    mt: 3,
} as const;